import type { CoreConfiguration, DropinConfiguration } from '@adyen/adyen-web';
import React, { useEffect, useRef, useState } from 'react';

import Text from '@sats-group/ui-lib/react/text';

import {
  initiateApplicationInsights,
  trackCustomException,
} from 'shared/application-insights/application-insights-web';
import PaymentSummary from 'shared/components/payment-summary/payment-summary';
import { type Core, useAdyen } from 'shared/hooks/use-adyen';
import type { NamedFC } from 'shared/types/named-fc.types';

import SplitLayout from 'ui/components/split-layout/split-layout';

import useAsyncEffect from './hooks/use-async-effect';
import getAdyenLocale from './utils/get-adyen-locale';
import type { Payment as Props } from './payment.props';

const Payment: NamedFC<Props> = ({
  adyenClientKey,
  adyenDisclaimer,
  adyenEnvironment,
  adyenTitle,
  applicationInsightsConnectionString,
  consent,
  language,
  layout,
  messages = [],
  onSuccessUrl,
  paymentErrorMessage,
  paymentModuleLoadingErrorMessage,
  paymentRefusedMessage,
  paymentSummary,
  session,
}) => {
  const [paymentMessages, setPaymentMessages] = useState(messages);
  const checkoutRef = useRef<Core>(undefined);

  const [, hasAdyenLoaded] = useAdyen(() =>
    setPaymentMessages([
      ...paymentMessages,
      {
        text: paymentModuleLoadingErrorMessage,
        theme: 'error',
      },
    ])
  );

  useEffect(
    () =>
      applicationInsightsConnectionString
        ? initiateApplicationInsights(applicationInsightsConnectionString)
        : undefined,
    [applicationInsightsConnectionString]
  );

  useAsyncEffect(async () => {
    if (!hasAdyenLoaded || !window.AdyenWeb || !onSuccessUrl) {
      return;
    }

    if (!session) {
      return;
    }

    const adyenConfiguration: CoreConfiguration = {
      environment: adyenEnvironment, // Change to 'live' for the live environment.
      clientKey: adyenClientKey, // Public key used for client-side authentication: https://docs.adyen.com/development-resources/client-side-authentication
      locale: getAdyenLocale(language),
      session: session,
      onPaymentCompleted: response => {
        switch (response.resultCode) {
          case 'Authorised': {
            window.location.assign(onSuccessUrl);
            return;
          }
          case 'Refused': {
            trackCustomException('Adyen payment refused', {
              resultCode: response.resultCode,
            });

            setPaymentMessages([
              {
                text: paymentRefusedMessage,
                theme: 'error',
              },
            ]);

            break;
          }
          case 'Error':
          default: {
            trackCustomException('Adyen payment error', {
              resultCode: response.resultCode,
            });

            setPaymentMessages([
              {
                text: paymentErrorMessage,
                theme: 'error',
              },
            ]);
          }
        }
      },
      onError: error => {
        trackCustomException(error, {
          source: 'adyen-on-error',
        });

        setPaymentMessages([
          {
            text: paymentErrorMessage,
            theme: 'error',
          },
        ]);
      },
    };

    const dropinConfiguration: DropinConfiguration = {
      openFirstPaymentMethod: true,
      openPaymentMethod: {
        type: 'applepay',
      },
      // Any payment method specific configuration. Find the configuration specific to each payment method:  https://docs.adyen.com/payment-methods
      // For example, this is 3D Secure configuration for cards:
      paymentMethodsConfiguration: {
        card: {
          challengeWindowSize: '05',
          name: adyenTitle,
          hasHolderName: true,
          holderNameRequired: true,
        },
      },
      showRadioButton: true,
    };

    const { AdyenCheckout, Dropin } = window.AdyenWeb;
    const checkout = await AdyenCheckout(adyenConfiguration);
    checkoutRef.current = checkout;
    new Dropin(checkout, dropinConfiguration).mount('#dropin-container');
    setPaymentMessages([]);
  }, [hasAdyenLoaded]);

  return (
    <SplitLayout messages={paymentMessages} {...layout}>
      <div className="payment">
        {paymentSummary ? <PaymentSummary {...paymentSummary} /> : null}
        <div id="dropin-container"></div>
        <div className="payment__disclaimer">
          <Text size={Text.sizes.interface}>{consent}</Text>
          <Text
            className="payment__adyen-disclaimer"
            size={Text.sizes.interface}
          >
            {adyenDisclaimer}
          </Text>
        </div>
      </div>
    </SplitLayout>
  );
};

Payment.displayName = 'Payment';

export default Payment;
